/**
 * Le mouvement de l'image de fond d'une séquence vidéo : ce que la page Mouvement de la
 * toolbox propose, traduit dans `seg.animation` tel que Remotion le rend (creatorstudio,
 * `AnimatedImage` : `kenBurns` zoome, `travellingLeft`/`travellingRight` balayent). Il
 * dure toute la séquence : pas de `duration`.
 *
 * Une seule amplitude, en pourcents, pour tous les mouvements : 30 donne le zoom 1 → 1,15
 * et le travelling 35 → 65 des templates (annonce_immo_safti, sixieme_avenue_cadre_video).
 * Le miroir Ruby, pour la page : CompositionDocument::SegmentMotion.
 */
export const MOTION_KINDS = ["none", "zoom_in", "zoom_out", "pan_left", "pan_right"] as const;
export type MotionKind = (typeof MOTION_KINDS)[number];

export const DEFAULT_AMPLITUDE = 30;
export const MIN_AMPLITUDE = 5;
export const MAX_AMPLITUDE = 100;

export interface SegmentAnimation {
  preset: string
  from?: number
  to?: number
  duration?: number
  speed?: number
}

export interface Motion {
  kind: MotionKind
  amplitude: number
}

const clampAmplitude = (a: number) => Math.round(Math.max(MIN_AMPLITUDE, Math.min(MAX_AMPLITUDE, a)));

/** L'animation d'un mouvement ; `null` pour aucun. */
export function motionAnimation(kind: MotionKind, amplitude = DEFAULT_AMPLITUDE): SegmentAnimation | null {
  const a = clampAmplitude(amplitude);
  const zoom = 1 + a / 200;
  const half = a / 2;
  switch (kind) {
    case "zoom_in": return { preset: "kenBurns", from: 1, to: zoom };
    case "zoom_out": return { preset: "kenBurns", from: zoom, to: 1 };
    case "pan_right": return { preset: "travellingRight", from: 50 - half, to: 50 + half };
    case "pan_left": return { preset: "travellingLeft", from: 50 + half, to: 50 - half };
    default: return null;
  }
}

/** Le mouvement qu'une animation décrit — au plus près pour une animation posée autrement
 *  (un template) : l'amplitude est celle de son écart. */
export function motionOf(animation: SegmentAnimation | null | undefined): Motion {
  if (!animation) return { kind: "none", amplitude: DEFAULT_AMPLITUDE };
  const from = animation.from;
  const to = animation.to;
  if (animation.preset === "kenBurns") {
    const start = from ?? 1;
    const end = to ?? 1.15;
    return { kind: end >= start ? "zoom_in" : "zoom_out", amplitude: clampAmplitude((Math.max(start, end) - 1) * 200) };
  }
  if (animation.preset === "travellingLeft" || animation.preset === "travellingRight") {
    const kind = animation.preset === "travellingLeft" ? "pan_left" : "pan_right";
    const span = from !== undefined && to !== undefined ? Math.abs(to - from) : animation.speed ? animation.speed * 5 : DEFAULT_AMPLITUDE;
    return { kind, amplitude: clampAmplitude(span) };
  }
  return { kind: "none", amplitude: DEFAULT_AMPLITUDE };
}
